import { useQuery } from '@tanstack/react-query'
import { useParams, useSearch } from '@tanstack/react-router'
import { ArrowLeft, Banknote, Sunrise, User } from 'lucide-react'
import { useState } from 'react'

import { savingsAccountQueryOptions, savingsProductListQueryOptions } from '@/api/savings-api'
import type { AccountHold } from '@/api/savings-types'
import { ErrorSurface } from '@/components/error-surface'
import { PageHeader } from '@/components/page-header'
import { StatusBadge } from '@/components/status-badge'
import { TabStrip, type TabDefinition } from '@/components/tab-strip'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  CloseAccountAction,
  CloseAccountDialog,
  ReactivateAccountDialog,
} from '@/features/savings/account-actions'
import {
  AccountOverviewPanel,
  BalanceSummary,
  HoldsPanel,
} from '@/features/savings/account-panels'
import { productClassLabel } from '@/features/savings/constants'
import { PlaceHoldDialog, ReleaseHoldDialog } from '@/features/savings/hold-dialogs'
import { savingsPaths, useSavingsNavigate } from '@/features/savings/navigation'
import { useSavingsPermissions } from '@/features/savings/permissions'
import { StatementPanel } from '@/features/savings/statement-panel'

import { savingsAccountDetailSearchSchema, type SavingsAccountTab } from './search'

const PANEL_ID = 'savings-account-panel'

type OpenDialog = 'placeHold' | 'releaseHold' | 'reactivate' | 'close' | null

/**
 * One savings account.
 *
 * The balances sit directly under the header, above the tabs, for the same reason the exposure
 * banner does on a customer: the available balance is the figure every decision on this screen
 * turns on, and it only works if it is read before the decision rather than found afterwards.
 *
 * The screen itself reads on a phone — the tables stack and the panels wrap. Two things on it do
 * not: keying a cash amount, which lives on its own screen and refuses a narrow display, and
 * closing the account, which pays the whole balance out in notes and is therefore withheld below
 * `lg` with the reason stated. Everything else here is a lookup or a stated reason.
 */
export function SavingsAccountDetailPage() {
  const params: Record<string, string | undefined> = useParams({ strict: false })
  const accountId = params.accountId ?? ''
  const navigate = useSavingsNavigate()
  const permissions = useSavingsPermissions()

  const search = savingsAccountDetailSearchSchema.parse(useSearch({ strict: false }))
  const tab: SavingsAccountTab = search.tab ?? 'overview'

  const [dialog, setDialog] = useState<OpenDialog>(null)
  const [releasing, setReleasing] = useState<AccountHold | null>(null)

  const query = useQuery(savingsAccountQueryOptions(accountId))

  /**
   * The product behind the account, for the terms panel. It is read from the product list rather
   * than from a product endpoint because there is none for a single product, and the list is
   * cached for ten minutes — so this is usually free. It is skipped entirely for a role without
   * `savings.product.view`, and the panel says so rather than showing an empty card.
   */
  const products = useQuery({
    ...savingsProductListQueryOptions({ includeInactive: true }),
    enabled: permissions.canViewProducts,
  })

  const selectTab = (next: SavingsAccountTab) => {
    navigate(savingsPaths.account(accountId), { search: { tab: next }, replace: true })
  }

  const closeDialog = () => setDialog(null)

  if (query.isPending) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-80" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-9 w-full max-w-md" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (query.isError) {
    return (
      <ErrorSurface
        error={query.error}
        onRetry={() => void query.refetch()}
        title="This account could not be opened"
      />
    )
  }

  const account = query.data
  const product = products.data?.find((candidate) => candidate.id === account.productId)
  const canPostAnything =
    permissions.canPostAnything && account.status === 'Active'

  return (
    <>
      <button
        type="button"
        onClick={() => navigate(savingsPaths.accounts())}
        className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-text hover:text-brand-text-hover max-lg:min-h-11 max-lg:py-2"
      >
        <ArrowLeft className="size-3.5" aria-hidden />
        All savings accounts
      </button>

      <PageHeader
        title={account.customerName}
        reference={account.accountNumber}
        badges={
          <>
            <StatusBadge status={account.status} />
            <Badge tone={account.productClass === 'SecurityDeposit' ? 'warning' : 'neutral'}>
              {productClassLabel(account.productClass)}
            </Badge>
          </>
        }
        actions={
          <>
            <Button
              variant="secondary"
              onClick={() => navigate(savingsPaths.customer(account.customerId))}
            >
              <User />
              Customer&apos;s savings
            </Button>
            {canPostAnything ? (
              <Button variant="primary" onClick={() => navigate(savingsPaths.accountCash(account.id))}>
                <Banknote />
                Post a transaction
              </Button>
            ) : null}
            {permissions.canManageDormancy && account.status === 'Dormant' ? (
              <Button variant="secondary" onClick={() => setDialog('reactivate')}>
                <Sunrise />
                Reactivate
              </Button>
            ) : null}
          </>
        }
      />

      <BalanceSummary account={account} />

      {account.status === 'Dormant' ? (
        <Alert tone="warning">
          <Sunrise aria-hidden />
          <div className="space-y-1">
            <AlertTitle>This account is dormant</AlertTitle>
            <AlertDescription>
              It has been idle past its product&apos;s dormancy period, so it refuses debits until it
              is reactivated. Money can still be paid in.
            </AlertDescription>
          </div>
        </Alert>
      ) : account.status === 'Frozen' ? (
        <Alert tone="danger">
          <Sunrise aria-hidden />
          <div className="space-y-1">
            <AlertTitle>This account is frozen</AlertTitle>
            <AlertDescription>
              Nothing posts to it. A supervisor has to release it — this screen cannot.
            </AlertDescription>
          </div>
        </Alert>
      ) : account.status === 'Closed' ? (
        <Alert tone="info">
          <Sunrise aria-hidden />
          <div className="space-y-1">
            <AlertTitle>This account is closed</AlertTitle>
            <AlertDescription>
              Closed {account.closedDate ?? 'on a date the record does not carry'}. It does not
              reopen; the statement below is kept for the record.
            </AlertDescription>
          </div>
        </Alert>
      ) : null}

      <div className="space-y-4">
        <TabStrip<SavingsAccountTab>
          tabs={
            [
              { id: 'overview', label: 'Overview' },
              { id: 'statement', label: 'Statement' },
              {
                id: 'holds',
                label: 'Holds',
                badge: account.holds.filter((hold) => hold.isActive).length,
              },
            ] satisfies TabDefinition<SavingsAccountTab>[]
          }
          active={tab}
          onChange={selectTab}
          panelId={PANEL_ID}
          label="Savings account sections"
        />

        <div id={PANEL_ID} role="tabpanel" aria-labelledby={`${PANEL_ID}-tab-${tab}`}>
          {tab === 'overview' ? (
            <AccountOverviewPanel account={account} product={product} />
          ) : null}
          {tab === 'statement' ? <StatementPanel account={account} /> : null}
          {tab === 'holds' ? (
            <HoldsPanel
              account={account}
              onPlaceHold={() => setDialog('placeHold')}
              onReleaseHold={(hold) => {
                setReleasing(hold)
                setDialog('releaseHold')
              }}
            />
          ) : null}
        </div>
      </div>

      {permissions.canClose && account.status !== 'Closed' ? (
        <div className="border-t border-border-subtle pt-4">
          <CloseAccountAction onClose={() => setDialog('close')} />
        </div>
      ) : null}

      <PlaceHoldDialog
        account={account}
        open={dialog === 'placeHold'}
        onOpenChange={(open) => {
          if (!open) {
            closeDialog()
          }
        }}
      />

      <ReleaseHoldDialog
        account={account}
        hold={releasing}
        open={dialog === 'releaseHold'}
        onOpenChange={(open) => {
          if (!open) {
            closeDialog()
            setReleasing(null)
          }
        }}
      />

      <ReactivateAccountDialog
        account={account}
        open={dialog === 'reactivate'}
        onOpenChange={(open) => {
          if (!open) {
            closeDialog()
          }
        }}
      />

      <CloseAccountDialog
        account={account}
        open={dialog === 'close'}
        onOpenChange={(open) => {
          if (!open) {
            closeDialog()
          }
        }}
      />
    </>
  )
}
